<template>

  <div class="container-fluid">
    
    <!-- Estado de carga -->
    <div v-if="loading" class="text-center py-5">
      <div class="loader-box">
        <div class="loader-3"></div>
      </div>
      <p>Cargando...</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="text-center py-5">
      <div class="alert alert-danger">
        <h5>Error al cargar los datos</h5>
        <p>{{ error.message || 'Ocurrió un error inesperado' }}</p>
        <button class="btn btn-primary" @click="fetchPost()">Reintentar</button>
      </div>
    </div>

    <!-- Contenido cuando hay datos -->
    <div v-else-if="estado != null">

      <div class="row starter-main">
        <div class="col-sm-12 mt-3 mb-3">
          <div class="card">

            <!-- Encabezado con logo y nombre del estado -->
            <div class="card-header d-flex align-items-center gap-3">
              <img
                v-if="estado.thumbnail_rendition"
                :src="estado.thumbnail_rendition.full_url"
                :alt="estado.thumbnail_rendition.alt"
                style="object-fit: contain; height: 64px; width: auto; flex-shrink: 0;"
              />
              <div>
                <h5 class="mb-0">{{ estado.title }}</h5>
              </div>
            </div>

            <!-- Pestañas de navegación -->
            <div class="card-header p-0 border-bottom-0" style="background: #f8f9fa;">
              <ul class="nav nav-tabs ficha-tabs px-3 pt-2">
                <li class="nav-item" v-for="tab in tabs" :key="tab.key">
                  <button
                    class="nav-link ficha-tab-btn"
                    :class="{ active: activeTab === tab.key }"
                    @click="activeTab = tab.key"
                  >{{ tab.label }}</button>
                </li>
              </ul>
            </div>

            <!-- Panel de pestañas -->
            <div class="card-body">

              <!-- Resumen territorial -->
              <div v-show="activeTab === 'resumen'">
                <div v-if="tabSections[0]" class="post-body" v-html="tabSections[0]"></div>
                <p v-else class="text-muted">Sin información disponible.</p>
              </div>

              <!-- Tabla de acciones estratégicas -->
              <div v-show="activeTab === 'acciones'">
                <div v-if="tabSections[1]" class="post-body" v-html="tabSections[1]"></div>
                <p v-else class="text-muted fst-italic">Contenido próximamente.</p>
              </div>

              <!-- Hoja de ruta estatal con indicadores sectoriales críticos -->
              <div v-show="activeTab === 'hojaRuta'">
                <div v-if="tabSections[2]" class="post-body" v-html="tabSections[2]"></div>
                <div v-if="hojaRutaPdfUrl" class="mt-3">
                  <iframe :src="hojaRutaPdfUrl" width="100%" style="height:80vh; border:none; border-radius:8px;"></iframe>
                </div>
                <p v-if="!tabSections[2] && !hojaRutaPdfUrl" class="text-muted fst-italic">Contenido próximamente.</p>
              </div>

              <!-- Vínculos con micrositios federales y estatales -->
              <div v-show="activeTab === 'vinculos'">
                <div v-if="tabSections[3]" class="post-body" v-html="tabSections[3]"></div>
                <p v-else class="text-muted fst-italic">Contenido próximamente.</p>
              </div>

            </div>

            <div class="card-footer" v-if="estado.footer">
              <p>Para mayor información <span v-html="estado.footer"></span></p>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import api from "@/axios";

export default {
  name: "FichaEstatal",
  data() {
    return {
      estado: null,
      processedBody: null,
      tabSections: [],
      error: null,
      loading: true,
      activeTab: 'resumen',
      tabs: [
        { key: 'resumen',   label: 'Resumen territorial' },
        { key: 'acciones',  label: 'Tabla de acciones estratégicas' },
        { key: 'hojaRuta',  label: 'Hoja de ruta' },
        { key: 'vinculos',  label: 'Vínculos con micrositios federales y estatales' },
      ],
    };
  },
  created() {
    this.fetchPost();
  },
  computed: {
    hojaRutaPdfUrl() {
      const section = this.tabSections[2];
      if (!section) return null;
      const match = section.match(/href="([^"]*\.pdf[^"]*)"/i);
      if (!match) return null;
      const url = match[1];
      return `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
    },
  },
  methods: {
    async fetchPost() {
      this.loading = true;
      this.error = null;
      const id = this.$route.params.idestado;
      try {
        const { data } = await api.get(`blog/${id}?fields=intro,body,thumbnail,thumbnail_rendition,footer`);
        this.estado = data;
        this.processedBody = data.body
          ? await this.resolveBodyEmbeds(data.body)
          : null;
        this.tabSections = this.splitByHr(this.processedBody);
      } catch (err) {
        this.error = err;
        console.error("error en fetchPost", err);
      } finally {
        this.loading = false;
      }
    },

    splitByHr(html) {
      if (!html) return [];
      // Split on any variant of <hr> and keep up to 4 sections; trim whitespace chunks
      return html
        .split(/<hr\s*\/?>/gi)
        .map(s => s.trim())
        .filter(s => s.length > 0)
        .slice(0, 4);
    },

    async resolveBodyEmbeds(body) {
      // --- Extraer IDs de imágenes embed ---
      const imageIds = [];
      const imageRegex = /<embed[^>]*embedtype="image"[^>]*id="(\d+)"[^>]*\/>/g;
      let m;
      while ((m = imageRegex.exec(body)) !== null) {
        if (!imageIds.includes(m[1])) imageIds.push(m[1]);
      }

      // --- Extraer IDs de documentos enlazados ---
      const docIds = [];
      const docRegex = /<a[^>]*linktype="document"[^>]*id="(\d+)"[^>]*>/g;
      while ((m = docRegex.exec(body)) !== null) {
        if (!docIds.includes(m[1])) docIds.push(m[1]);
      }

      // --- Consultar imágenes y documentos en paralelo ---
      const imageMap = {};
      const docMap = {};

      await Promise.all([
        ...imageIds.map(async (imgId) => {
          try {
            const { data } = await api.get(`images/${imgId}/`);
            imageMap[imgId] = data;
          } catch (e) {
            console.error(`Error al obtener imagen ${imgId}`, e);
          }
        }),
        ...docIds.map(async (docId) => {
          try {
            const { data } = await api.get(`documents/${docId}/`);
            docMap[docId] = data;
          } catch (e) {
            console.error(`Error al obtener documento ${docId}`, e);
          }
        }),
      ]);

      // --- Reemplazar embeds de imagen por <img> ---
      let result = body.replace(
        /<embed[^>]*embedtype="image"[^>]*id="(\d+)"[^>]*alt="([^"]*)"[^>]*\/>/g,
        (fullMatch, imgId, alt) => {
          const img = imageMap[imgId];
          if (!img) return '';
          const downloadUrl = img.meta?.download_url ?? '';
          const src = downloadUrl.startsWith('http')
            ? downloadUrl
            : `https://rverdeadmin.klatus.systems${downloadUrl}`;
          const altText = alt || img.title || '';
          return `<img src="${src}" alt="${altText}" class="img-fluid my-3" style="max-width: 80%; display: block; margin-left: auto; margin-right: auto;" />`;
        }
      );

      // --- Reemplazar enlaces de documento por <a href> ---
      result = result.replace(
        /<a[^>]*linktype="document"[^>]*id="(\d+)"[^>]*>(.*?)<\/a>/gs,
        (fullMatch, docId, linkText) => {
          const doc = docMap[docId];
          if (!doc) return linkText;
          const downloadUrl = doc.meta?.download_url ?? '';
          const href = downloadUrl.startsWith('http')
            ? downloadUrl
            : `https://rverdeadmin.klatus.systems${downloadUrl}`;
          const title = linkText || doc.title || 'Documento';
          return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="btn btn-success btn-sm my-1"><i class="fa fa-file-pdf-o me-1"></i>${title}</a>`;
        }
      );

      return result;
    },
  },
};
</script>

<style scoped>
.ficha-tab-btn {
  font-size: 0.9rem;
  color: #555;
  border: none;
  border-bottom: 3px solid transparent;
  background: transparent;
  border-radius: 0;
  padding: 0.55rem 1rem;
  margin-right: 4px;
  transition: color 0.2s, border-color 0.2s;
}
.ficha-tab-btn:hover {
  color: #182e50;
  border-bottom-color: #182e50;
  background: transparent;
}
.ficha-tab-btn.active {
  color: #fff !important;
  background-color: #182e50 !important;
  border-color: #182e50 #182e50 #fff !important;
  border-radius: 6px 6px 0 0;
  font-weight: 600;
}
</style>
